<template>
  <div>
    <Label class="mt-2" required>
      {{ $t("create_pass_student_view.destination") }}
    </Label>
    <LazyLoadSelect
      v-model="state.form.to"
      :invalid-feedback="errors.to ? $t(errors.to) : ''"
      :is-multiple="false"
      :selected="state.form.to"
      type="combined"
      :adult-pass-request-disabled="true"
      :is-s-l-p="true"
      :placeholder="$t('create_pass_student_view.teacher_location_placeholder')"
      :force-clear-cache="true"
      @changed="(value) => onChangeEvent(value)"
    >
    </LazyLoadSelect>

    <InfoBox v-if="destinationUnavailability" class="danger mt-2 mb-2">
      {{
        destinationUnavailability.unavailable_type == "App\\Models\\Room"
          ? $t("create_pass_student_view.this_room_is_unavailable_until")
          : $t("create_pass_student_view.this_adult_is_unavailable_until")
      }}
      <span
        class="mx-1"
        v-html="
          $helpers.getHTMLDateTime(
            destinationUnavailability.to_date,
            'MMMM D, YYYY',
            'h:mm A'
          ) + '.'
        "
      />
      <span v-if="destinationUnavailability.comment"
        >{{ $t("common.note") }}: {{ destinationUnavailability.comment }}</span
      >
    </InfoBox>

    <InfoBox class="danger mt-2 mb-2" v-if="state.serverErrors.to_id">
      {{ state.serverErrors.to_id[0] }}
    </InfoBox>

    <FavoriteCarousel
      :key="'destination'"
      :selected="state.form.to"
      :is-editable="true"
      :show-unavailables="true"
      :role="currentUser.role.name"
      :current-user="currentUser"
      :isvalid="errors.to"
      section="destination"
      :action="
        (elem) => {
          onChangeEvent(elem)
        }
      "
    />

    <div
      v-if="
        state.form &&
        state.form.to &&
        state.form.to.value &&
        state.form.to.value.comment_type != 'Hidden'
      "
      class="mb-3"
    >
      <Label> {{ $t("common.reason") }} </Label>
      <InputField
        v-model="state.form.reason"
        :invalid-feedback="errors.reason ? $t(errors.reason) : ''"
        max-chars="250"
        :rows="5"
        class="w-full"
        type="textarea"
        :placeholder="
          state.form.to.value.comment_type == 'Optional'
            ? $t('create_pass_student_view.reason_for_pass_optional')
            : $t('create_pass_student_view.reason_for_pass_required')
        "
      />
      <InfoBox v-if="state.serverErrors.comment" class="danger mt-2"
        >{{ state.serverErrors.comment[0] }}
      </InfoBox>
    </div>

    <div class="tardy-late-pass d-flex flex-column mt-2">
      <p class="mb-1">
        {{ $t("create_pass_student_view.mark_this_pass_as") }}
      </p>
      <TardyRadioGroup
        v-model="state.form.tardy_badge"
        :is-allowed-lates="true"
        :allowed-lates="allowedLates"
        :disabled="isTardyGroupDisabled"
      />
      <div v-if="state.isTardyRequired" class="invalid-feedback">
        {{ $t("common.this_option_is_required") }}
      </div>
    </div>
    <div v-if="state.serverErrors.tardy_badge" class="invalid-feedback">
      {{ state.serverErrors.tardy_badge[0] }}
    </div>

    <InfoBox
      v-if="state.serverRes"
      class="mt-3"
      :class="{ danger: Object.keys(state.serverErrors).length }"
      :title="state.serverRes.title"
      >{{ state.serverRes.message }}</InfoBox
    >

    <div class="form-group form-actions mt-2 text-center">
      <div class="d-inline" @click="submit()">
        <LoadingButton :is-loading="state.isLoading" class="mt-2" rounded solid>
          {{ $t("common.submit") }}
        </LoadingButton>
      </div>
    </div>
  </div>
</template>

<script>
import FavoriteCarousel from "@/v3components/FavoriteCarousel.vue"
import TardyRadioGroup from "@/v3components/Forms/TardyRadioGroup.vue"
import InfoBox from "@/v3components/shared/Alerts/InfoBox.vue"
import LoadingButton from "@/v3components/shared/Buttons/LoadingButton.vue"
import InputField from "@/v3components/shared/Form/InputField.vue"
import Label from "@/v3components/shared/Form/Label.vue"
import LazyLoadSelect from "@/v3components/shared/Form/LazyLoadSelect.vue"
import { useVuelidate } from "@vuelidate/core"
import { helpers, required, requiredIf } from "@vuelidate/validators"
import { computed, onMounted, reactive, watch } from "vue"
import { useI18n } from "vue-i18n"
import { useRouter } from "vue-router"
import { useStore } from "vuex"

export default {
  name: "CreateLatePassForm",
  components: {
    LazyLoadSelect,
    FavoriteCarousel,
    Label,
    InputField,
    LoadingButton,
    TardyRadioGroup,
    InfoBox
  },
  setup() {
    const store = useStore()
    const { t } = useI18n()
    const router = useRouter()

    const state = reactive({
      isLoading: false,
      form: {
        to: [],
        reason: null,
        tardy_badge: null
      },
      serverErrors: {},
      serverRes: null,
      isTardyRequired: false
    })

    const currentUser = computed(() => store.getters["authentication/user"])

    const activeSchoolModules = computed(
      () => store.getters["schools/activeSchoolModules"]
    )

    const destinationUnavailability = computed(() => {
      return state.form.to && state.form.to.value
        ? state.form.to.value.unavailability
        : null
    })

    const allowedLates = computed(() => {
      const tardyModule = activeSchoolModules.value.find(
        (module) => module.name === "Tardy Management" && module.pivot.status
      )
      let tardyModuleOptions
      if (tardyModule) {
        tardyModuleOptions = JSON.parse(tardyModule.pivot.option_json)
      }
      const lts = tardyModuleOptions["allow-students-create"].lts
      const ltc = tardyModuleOptions["allow-students-create"].ltc
      return {
        lts: lts,
        ltc: ltc
      }
    })

    const isTardyGroupDisabled = computed(() => {
      return allowedLates.value.lts && allowedLates.value.ltc ? false : true
    })

    const validationMessages = {
      required: "common.this_field_is_required",
      requiredReason: "create_pass_student_view.reason_for_pass_required"
    }

    const validations = computed(() => ({
      form: {
        to: {
          available: () => {
            if (
              state.form.to &&
              state.form.to.value &&
              !state.form.to.value.unavailability
            ) {
              return true
            }
          },
          required: helpers.withMessage(validationMessages.required, required)
        },
        reason: {
          required: helpers.withMessage(
            validationMessages.requiredReason,
            requiredIf(() => {
              if (state.form.to && state.form.to.value) {
                return state.form.to.value.comment_type == "Optional"
                  ? false
                  : state.form.to.value.comment_type == "Hidden"
                    ? false
                    : true
              }
            })
          )
        }
      }
    }))

    const v$ = useVuelidate(validations.value.form, state.form)

    const isValid = computed(() => !v$.value.$invalid)

    const errors = computed(() => {
      const errorObj = {}
      v$.value.$errors.forEach((err) => {
        errorObj[err.$property] = err.$message
      })
      return errorObj
    })

    onMounted(() => {
      updateTardyBadge()
    })

    const onChangeEvent = (value) => {
      state.form.to = value

      store
        .dispatch("rooms/getUnavailability", {
          id: value.value.id,
          type: value.value.type
        })
        .then((response) => {
          if (response.data.data) {
            state.form.to.value.unavailability = response.data.data
          }
        })
    }

    const submit = () => {
      state.isTardyRequired = false
      setResponseInfoBox()
      if (isValid.value && state.form.tardy_badge !== null) {
        const pass = {
          to_id: state.form.to.value.id,
          to_type: state.form.to.value.type,
          comment: state.form.reason,
          tardy_badge: state.form.tardy_badge
        }
        state.isLoading = true
        store
          .dispatch("passes/createLatePass", pass)
          .then(() => {
            state.serverErrors = {}
            setResponseInfoBox(
              "Success",
              t("create_pass_student_view.successfully_created")
            )

            setTimeout(() => {
              setResponseInfoBox()
              resetForm()
              router.push("/passes/activepass")
              state.isLoading = false
            }, 500)
          })
          .catch((err) => {
            state.isLoading = false
            const response = err?.response?.data ? err.response.data : err
            state.serverErrors = response?.errors ? response.errors : {}
            setResponseInfoBox("Error", response?.message)
          })
      } else {
        v$.value.$touch()
        if (!state.form.tardy_badge) {
          state.isTardyRequired = true
        }
      }
    }

    const resetForm = () => {
      state.form = {
        to: [],
        reason: null,
        tardy_badge: null
      }
      state.isTardyRequired = false
    }

    const setResponseInfoBox = (title, message) => {
      if (title || message) {
        state.serverRes = {
          message,
          title
        }
      } else {
        state.serverRes = null
      }
    }

    const updateTardyBadge = () => {
      if (allowedLates.value.lts !== allowedLates.value.ltc) {
        if (allowedLates.value.lts) {
          state.form.tardy_badge = "LTS"
          state.isTardyRequired = false
        } else {
          state.form.tardy_badge = "LTC"
          state.isTardyRequired = false
        }
      } else {
        state.form.tardy_badge = null
      }
    }

    watch(
      () => allowedLates.value,
      () => {
        updateTardyBadge()
      }
    )

    return {
      state,
      destinationUnavailability,
      onChangeEvent,
      submit,
      allowedLates,
      isTardyGroupDisabled,
      errors,
      currentUser,
      t
    }
  }
}
</script>

<style></style>
